import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../styles/ProjectDescPage.css";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import { Loader } from "lucide-react";

interface Project {
  id: number;
  name: string;
  description: string;
  image: string;
  platforms: string[];
  stack: string[];
  features: string[];
  links: {
    github?: string | null;
    website?: string | null;
    apk?: string | null;
    [key: string]: string | null | undefined;
  };
}

function ProjectDescPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const backend = "https://personal-portfolio-backend-waf6.vercel.app";

  const readableLinks: { [key: string]: string } = {
    github: "GitHub Repository",
    website: "Project Website",
    apk: "Download APK",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({
      duration: 500,
      offset: 200,
      easing: "ease-in-out",
      once: true,
      mirror: true,
    });

    const fetchProject = async () => {
      try {
        const response = await axios.get<Project[]>(
          `${backend}/content/projects.json`
        );
        const selected = response.data.find(
          (p) => p.id.toString() === projectId
        );
        if (selected) {
          setProject(selected);
        } else {
          setError("Project not found.");
        }
      } catch (err) {
        setError("Error fetching project details.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  return (
    <div className="projectDescPage">
      {error && <p className="message">{error}</p>}
      {loading ? (
        <div className="projectsLoader" data-aos="fade-up">
          <Loader />
        </div>
      ) : project ? (
        <div className="projectContent" data-aos="fade-up" data-aos-delay="300">
          <div className="projectTitle" data-aos="fade-up">
            {project.name}
          </div>
          <div className="projectTable" data-aos="fade-up">
            <img
              src={`${backend}/photos/${project.image}`}
              alt={project.name}
              className="projectImage"
            />
            <div className="projectTools">
              <div className="projectPlatforms">
                <div className="projectPlatformsTitle">Platforms</div>
                <ul className="commaList">
                  {project.platforms?.map((platform) => (
                    <li key={platform}>{platform}</li>
                  ))}
                </ul>
              </div>

              <div className="projectLanguages">
                <div className="projectLanguagesTitle">Tech stack</div>
                <ul className="projectList">
                  {project.stack?.map((tech) => (
                    <li key={tech}>{tech}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="projectKeyFeatures">
              <div className="projectKeyFeaturesTitle">Key features</div>
              <ul className="projectList">
                {project.features?.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="projectDescription">
            <div className="projectDescriptionTitle">Description</div>
            {project.description}
          </div>
          <div className="projectLinks">
            Links
            <ul className="projectLinksList">
              {project.links &&
                Object.entries(project.links).map(([key, url]) =>
                  url ? (
                    <li key={key}>
                      <a
                        className="projectLinksButton"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {readableLinks[key] || key}
                      </a>
                    </li>
                  ) : null
                )}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProjectDescPage;
